import { useEffect, useState } from 'react'
import { FormProvider, useForm, useFormContext } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { ImageIcon, Plus, Trash2 } from 'lucide-react'
import type { FieldErrors } from 'react-hook-form'

import type {
    BlockForm,
    ImageValue,
    ItemForm,
    LinkForm,
} from '@/components/retail/home-form-model'
import type { ShopHomeKind, ShopHomeSection } from '@/lib/api/shop-content-api'
import {
    ITEM_MAX,
    blockSchema,
    blockToForm,
    blockToInput,
    hasBlockSchedule,
    hasItemSchedule,
    isListKind,
    isWebOnly,
    itemIndexOf,
    kindLabel,
    linkSummary,
    newItem,
    scheduleText,
} from '@/components/retail/home-form-model'
import {
    ScheduleFields,
    SortableList,
    SortableRow,
    useArrayField,
} from '@/components/retail/home-form-bits'
import {
    GiftBannerFields,
    HeroSlideFields,
    HomeTabFields,
    OcopFields,
    PromoFields,
    StoreSystemFields,
    StoryFields,
} from '@/components/retail/home-content-blocks'
import { useCatalogNames } from '@/components/retail/home-link-field'
import { ErrorText, SwitchField } from '@/components/retail/form-bits'
import { setShopServerErrors } from '@/components/retail/shop-errors'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { FieldError } from '@/components/ui/field'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { useSaveShopHomeSection } from '@/hooks/use-shop-content-query'
import { ApiError } from '@/lib/api/client'
import { thumbOf } from '@/lib/api/shop-catalog-api'
import { m } from '@/lib/paraglide/messages'
import { cn } from '@/lib/utils'

interface Props {
    section: ShopHomeSection
    canWrite: boolean
    /** Báo trang khi form có thay đổi chưa lưu (hỏi trước khi đổi khối). */
    onDirtyChange: (dirty: boolean) => void
    /** Tải lại dữ liệu sau 409 (người khác vừa lưu khối này). */
    onReload: () => void
}

/**
 * Form một khối trang chủ: cờ khối (Hiện web / Hiện Mini App / Bật), dữ liệu
 * khối hoặc danh sách dòng, nút Lưu riêng (PUT cả khối). Trang gắn key theo
 * kind + version nên lưu xong (version mới) form tự nạp lại.
 */
export function HomeBlockForm({
    section,
    canWrite,
    onDirtyChange,
    onReload,
}: Props) {
    const kind = section.kind
    const form = useForm<BlockForm>({
        resolver: zodResolver(blockSchema(kind)),
        defaultValues: blockToForm(section),
    })
    const save = useSaveShopHomeSection()
    const [editing, setEditing] = useState<string | null>(null)
    const [conflict, setConflict] = useState(false)
    const { isDirty } = form.formState

    useEffect(() => {
        onDirtyChange(isDirty)
    }, [isDirty, onDirtyChange])

    // Lỗi nằm trong một dòng đang đóng → mở dòng đó để thấy lỗi.
    const openRow = (index: number | null) => {
        const row = index === null ? undefined : form.getValues('items')[index]
        if (row) setEditing(row.key)
    }

    const onInvalid = (errors: FieldErrors<BlockForm>) => {
        if (Array.isArray(errors.items))
            openRow(errors.items.findIndex(Boolean))
    }

    const submit = form.handleSubmit(async (values) => {
        setConflict(false)
        try {
            await save.mutateAsync({
                kind,
                input: blockToInput(kind, section.version, values),
            })
        } catch (err) {
            setShopServerErrors(form.setError, err, m.shop_home_save_failed())
            if (err instanceof ApiError) {
                setConflict(err.code === 'SHOP_CONCURRENT_CHANGE')
                openRow(itemIndexOf(err.params?.field))
            }
        }
    }, onInvalid)

    const webOnly = isWebOnly(kind)

    return (
        <FormProvider {...form}>
            <form onSubmit={submit} noValidate>
                <Card>
                    <CardHeader className="space-y-3">
                        <CardTitle
                            role="heading"
                            aria-level={2}
                            className="text-base"
                        >
                            {kindLabel(kind)}
                        </CardTitle>
                        <fieldset
                            disabled={!canWrite}
                            className="flex flex-wrap items-center gap-x-6 gap-y-2"
                        >
                            <SwitchField
                                name="show_web"
                                label={m.shop_show_web()}
                            />
                            {webOnly ? (
                                <div className="flex items-center gap-2">
                                    <Switch
                                        id="show_miniapp"
                                        checked={false}
                                        disabled
                                        aria-describedby="miniapp-none"
                                    />
                                    <Label htmlFor="show_miniapp">
                                        {m.shop_show_miniapp()}
                                    </Label>
                                    <span
                                        id="miniapp-none"
                                        className="text-xs text-muted-foreground"
                                    >
                                        {m.shop_home_miniapp_none()}
                                    </span>
                                </div>
                            ) : (
                                <SwitchField
                                    name="show_miniapp"
                                    label={m.shop_show_miniapp()}
                                />
                            )}
                            <SwitchField
                                name="enabled"
                                label={m.shop_home_enabled()}
                            />
                        </fieldset>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        {isListKind(kind) ? (
                            // Chỉ xem vẫn mở được dòng để đọc: danh sách tự khoá
                            // từng ô, không bọc fieldset disabled.
                            <ItemList
                                kind={kind}
                                canWrite={canWrite}
                                editing={editing}
                                onEdit={setEditing}
                            />
                        ) : (
                            <fieldset
                                disabled={!canWrite}
                                className="space-y-4"
                            >
                                {hasBlockSchedule(kind) && (
                                    <ScheduleFields prefix="" />
                                )}
                                <BlockFields kind={kind} canWrite={canWrite} />
                            </fieldset>
                        )}
                        {kind === 'hero_slide' && (
                            <p className="text-xs text-muted-foreground">
                                {m.shop_home_hero_note()}
                            </p>
                        )}
                        <ErrorText
                            text={form.formState.errors.root?.server?.message}
                        />
                        {conflict && (
                            <Button
                                type="button"
                                size="sm"
                                variant="outline"
                                onClick={onReload}
                            >
                                {m.shop_home_reload()}
                            </Button>
                        )}
                        {canWrite && (
                            <div className="flex justify-end">
                                <Button type="submit" disabled={save.isPending}>
                                    {save.isPending
                                        ? m.common_saving()
                                        : m.shop_home_save_block()}
                                </Button>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </form>
        </FormProvider>
    )
}

function BlockFields({
    kind,
    canWrite,
}: {
    kind: ShopHomeKind
    canWrite: boolean
}) {
    switch (kind) {
        case 'gift_banner':
            return <GiftBannerFields />
        case 'story':
            return <StoryFields canWrite={canWrite} />
        case 'ocop':
            return <OcopFields canWrite={canWrite} />
        default:
            return <StoreSystemFields canWrite={canWrite} />
    }
}

/** Ảnh nhỏ + chữ chính của một dòng. */
function rowText(kind: ShopHomeKind, d: Record<string, unknown>) {
    const title = typeof d.title === 'string' ? d.title : ''
    if (kind === 'hero_slide') {
        const tab = typeof d.tab === 'string' ? d.tab : ''
        return [tab, title].filter(Boolean).join(' — ')
    }
    return title
}

function ItemList({
    kind,
    canWrite,
    editing,
    onEdit,
}: {
    kind: ShopHomeKind
    canWrite: boolean
    editing: string | null
    onEdit: (key: string | null) => void
}) {
    const { setValue, formState, clearErrors } = useFormContext<BlockForm>()
    const [items, setItems] = useArrayField<ItemForm>('items')
    const names = useCatalogNames()
    const max = ITEM_MAX[kind] ?? 0
    const webOnly = isWebOnly(kind)
    const scheduled = hasItemSchedule(kind)
    const itemErrors = formState.errors.items
    const now = new Date()
    const editIndex = items.findIndex((it) => it.key === editing)

    const reorder = (next: ItemForm[]) => {
        // Lỗi gắn theo chỉ số dòng — đổi thứ tự thì bỏ lỗi cũ.
        clearErrors('items')
        setItems(next)
    }
    const add = () => {
        const row = newItem(kind)
        setItems([...items, row])
        onEdit(row.key)
    }
    const remove = (key: string) => {
        clearErrors('items')
        setItems(items.filter((it) => it.key !== key))
        onEdit(null)
    }
    const flag = (i: number, key: 'enabled' | 'show_web' | 'show_miniapp') =>
        setValue(`items.${i}.${key}`, !items[i][key], { shouldDirty: true })

    return (
        <div className="space-y-3">
            {items.length === 0 && (
                <p className="py-4 text-center text-sm text-muted-foreground">
                    {m.shop_home_rows_empty()}
                </p>
            )}
            <SortableList rows={items} onChange={reorder}>
                {items.map((it, i) => {
                    const d = it.data
                    const image = d.image as ImageValue | null | undefined
                    const text = rowText(kind, d) || m.shop_home_row_untitled()
                    const sched = scheduled
                        ? scheduleText(it.starts_at, it.ends_at, now)
                        : null
                    const hasError =
                        Array.isArray(itemErrors) && !!itemErrors[i]
                    return (
                        <SortableRow
                            key={it.key}
                            id={it.key}
                            label={text}
                            canWrite={canWrite}
                            className={cn(
                                'rounded-md border p-2',
                                editing === it.key && 'border-primary',
                                hasError && 'border-destructive',
                            )}
                        >
                            <div
                                className={cn(
                                    'flex flex-wrap items-center gap-3',
                                    sched?.expired && 'opacity-50',
                                )}
                            >
                                {kind !== 'home_tab' && (
                                    <div className="flex size-12 shrink-0 items-center justify-center overflow-hidden rounded bg-muted">
                                        {image?.url ? (
                                            <img
                                                src={
                                                    image.thumb_url ||
                                                    thumbOf(image.url)
                                                }
                                                alt=""
                                                className="size-full object-cover"
                                            />
                                        ) : (
                                            <ImageIcon className="size-5 text-muted-foreground" />
                                        )}
                                    </div>
                                )}
                                <div className="min-w-0 flex-1 basis-40">
                                    <p className="truncate text-sm font-medium">
                                        {i + 1}. {text}
                                    </p>
                                    <p className="truncate text-xs text-muted-foreground">
                                        {linkSummary(d.link as LinkForm, names)}
                                    </p>
                                    {sched && (
                                        <p className="text-xs text-muted-foreground">
                                            {sched.text}
                                        </p>
                                    )}
                                    {hasError && (
                                        <p className="text-xs text-destructive">
                                            {m.shop_home_row_has_error()}
                                        </p>
                                    )}
                                </div>
                                <div className="flex flex-wrap items-center gap-3 text-xs">
                                    <RowSwitch
                                        label={m.shop_home_chip_web()}
                                        aria={m.shop_toggle_web_aria({
                                            name: text,
                                        })}
                                        checked={it.show_web}
                                        readOnly={!canWrite}
                                        onToggle={() => flag(i, 'show_web')}
                                    />
                                    <RowSwitch
                                        label={m.shop_home_chip_miniapp()}
                                        aria={
                                            webOnly
                                                ? m.shop_home_miniapp_none()
                                                : m.shop_toggle_miniapp_aria({
                                                      name: text,
                                                  })
                                        }
                                        checked={
                                            webOnly ? false : it.show_miniapp
                                        }
                                        locked={webOnly}
                                        readOnly={!canWrite}
                                        onToggle={() => flag(i, 'show_miniapp')}
                                    />
                                    <RowSwitch
                                        label={m.shop_home_enabled()}
                                        aria={m.shop_home_row_enabled_aria({
                                            name: text,
                                        })}
                                        checked={it.enabled}
                                        readOnly={!canWrite}
                                        onToggle={() => flag(i, 'enabled')}
                                    />
                                    <Button
                                        type="button"
                                        size="sm"
                                        variant="ghost"
                                        aria-expanded={editing === it.key}
                                        onClick={() =>
                                            onEdit(
                                                editing === it.key
                                                    ? null
                                                    : it.key,
                                            )
                                        }
                                    >
                                        {canWrite
                                            ? m.common_edit()
                                            : m.shop_home_view()}
                                    </Button>
                                </div>
                            </div>
                        </SortableRow>
                    )
                })}
            </SortableList>
            <FieldError name="items" />
            {canWrite &&
                (items.length < max ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={add}
                    >
                        <Plus className="mr-1 size-4" />
                        {kind === 'hero_slide'
                            ? m.shop_home_add_slide()
                            : m.shop_home_add_row()}
                    </Button>
                ) : (
                    <p className="text-xs text-muted-foreground">
                        {m.shop_home_max_rows({ max: String(max) })}
                    </p>
                ))}
            {editIndex >= 0 && (
                <ItemEditor
                    kind={kind}
                    index={editIndex}
                    canWrite={canWrite}
                    onClose={() => onEdit(null)}
                    onRemove={() => remove(items[editIndex].key)}
                />
            )}
        </div>
    )
}

function RowSwitch({
    label,
    aria,
    checked,
    locked,
    readOnly,
    onToggle,
}: {
    label: string
    aria: string
    checked: boolean
    locked?: boolean
    readOnly: boolean
    onToggle: () => void
}) {
    return (
        <span className="flex items-center gap-1.5">
            <Switch
                checked={checked}
                disabled={locked || readOnly}
                aria-label={aria}
                onCheckedChange={onToggle}
            />
            <span aria-hidden="true">{locked ? '—' : label}</span>
        </span>
    )
}

/** Ô sửa một dòng (mở ngay dưới danh sách); lưu cùng khối bằng nút Lưu khối. */
function ItemEditor({
    kind,
    index,
    canWrite,
    onClose,
    onRemove,
}: {
    kind: ShopHomeKind
    index: number
    canWrite: boolean
    onClose: () => void
    onRemove: () => void
}) {
    const p = `items.${index}`
    return (
        <section
            aria-label={m.shop_home_row_editor({ n: String(index + 1) })}
            className="space-y-4 rounded-md border border-primary/40 bg-muted/20 p-3"
        >
            <h3 className="text-sm font-semibold">
                {m.shop_home_row_editor({ n: String(index + 1) })}
            </h3>
            <fieldset disabled={!canWrite} className="space-y-4">
                {kind === 'hero_slide' ? (
                    <HeroSlideFields p={`${p}.data`} />
                ) : kind === 'promo' ? (
                    <PromoFields p={`${p}.data`} />
                ) : (
                    <HomeTabFields p={`${p}.data`} />
                )}
                {hasItemSchedule(kind) && <ScheduleFields prefix={`${p}.`} />}
            </fieldset>
            <p className="text-xs text-muted-foreground">
                {m.shop_home_row_save_note()}
            </p>
            <div className="flex flex-wrap justify-between gap-2">
                {canWrite ? (
                    <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={onRemove}
                    >
                        <Trash2 className="mr-1 size-4" />
                        {m.shop_home_remove_row()}
                    </Button>
                ) : (
                    <span />
                )}
                <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={onClose}
                >
                    {m.shop_home_row_done()}
                </Button>
            </div>
        </section>
    )
}
